import { ListenerEffectAPI, TaskAbortError } from "@reduxjs/toolkit";
import * as Comlink from "comlink";

import type { AppStartListening } from "@/data/listenerMiddleware";
import { onFocus, onOnline } from "@/data/setup-dom-listeners";
import type { AppDispatch, RootState } from "@/data/store";
import { DateNotifierWorkerProxy } from "@/features/timeline";
import { intervalInMs } from "@/utils/datetime";

// Select a Date from the Timeline slice state.
type DateSelector = (state: RootState) => WatchedDate;

export interface WatchedDate {
  date: string | null;
  passed?: boolean;
}

/**
 * Create a Redux listener which starts a timer counting down towards a date
 * from the `timeline` slice.
 *
 * The Redux state date is selected using the `selectDate` callback. If the date
 * changes and has not been marked as passed, the timer is restarted.
 *
 * The timer is run in a worker thread using the `DateNotifierWorkerProxy`.
 *
 * At the end of the timer `callback` is invoked and the effect is cancelled.
 * Only 1 timer will run for the selected date.
 *
 * To start the listener, pass the `AppStartListening` function from the Redux
 * store's listener middleware, to the function returned here.
 *
 * @param selectDate Redux selector to extract a timeline date with `date` and
 *   `passed` properties.
 * @param callback A callback invoked when the date passes.
 *
 * @return A function `(startListening) => any` which can be called to start the
 *   global listener.
 */
export const createDateListener =
  (
    selectDate: DateSelector,
    callback: (listenerAPI: ListenerEffectAPI<RootState, AppDispatch>) => void
  ) =>
  (startListening: AppStartListening) => {
    startListening({
      predicate: (action, currentState, previousState) => {
        // Re-fire the effect on window focus and on network status change to online
        if (onFocus.match(action) || onOnline.match(action)) {
          return true;
        }
        const curr = selectDate(currentState);
        const prev = selectDate(previousState);
        return curr.date !== null && !curr.passed && curr.date !== prev.date;
      },
      effect: async (_action, listenerApi) => {
        const timelineDate = selectDate(listenerApi.getState());
        const date = timelineDate.date;

        if (date === null || timelineDate.passed) return undefined;

        // Only allow one instance of this listener to run at a time
        listenerApi.cancelActiveListeners();

        // Create a callback with proxy which dispatches the `action` on timer end
        const onNotify = () => {
          if (!listenerApi.signal.aborted) {
            callback(listenerApi);
          }
        };

        // Use the worker proxy to start a new timer instance in the worker.
        const timeout = intervalInMs(new Date(date));
        if (timeout < 0) {
          onNotify();
          return undefined;
        }

        const onNotifyProxy = Comlink.proxy(onNotify);
        const instance = await new DateNotifierWorkerProxy(
          onNotifyProxy,
          timeout
        );

        // Start the worker timer if the listener task is active
        if (!listenerApi.signal.aborted) {
          await instance.tick();
        }

        try {
          // Wait for the watched date to pass. Using `condition` here also lets
          // us be cancellation aware.
          await listenerApi.condition(
            (_, state) => selectDate(state)?.passed === true
          );
        } catch (err) {
          // Task is cancelled, clear any running timers before exiting
          if (err instanceof TaskAbortError) {
            await instance.stopTimer();
          }
        }
      },
    });
  };
